import {
  useCreateOrUpdateDocument,
  useDeleteDocument,
  useDocuments,
} from '@/hooks/queries/documents';
import axios from 'axios';

import { useLoaderStore } from '@/store/use-loader-store';
import { useDocumentStore } from '@/store/useDocumentStore';
import {
  DocumentDataType,
  SnapshotRequestType,
  UploadFileDetailType,
} from '@/types/doc';
import { Icon } from '@iconify/react';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Tab,
  Tabs,
} from '@nextui-org/react';
import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import DocumentService from 'Src/services/document-service';
import SnapshotService from 'Src/services/snapshot-service';
import { useAuthStore } from 'Src/store/use-auth-store';

interface SettingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const constructPostDataForFileUpload = (data: UploadFileDetailType[]) => ({
  batch_id: data[0].batchId,
  org_id: data[0].orgId,
  files: data.map((file) => ({
    document_id: file.docId,
    filename: file.name,
    file_original_name: file.file.name,
    file_mimetype: file.file.type,
  })),
});

export default function SettingsDrawer({
  isOpen,
  onClose,
}: SettingsDrawerProps) {
  const orgId = useAuthStore((state) => state.orgId);

  const setIsLoading = useLoaderStore((state) => state.setIsLoading);
  const { selectedFiles, setSelectedFiles } = useDocumentStore();

  const createOrUpdateDocument = useCreateOrUpdateDocument({
    onSuccess: () => {
      //
    },
  });
  const { refetch: refetchDocuments, data } = useDocuments();

  const deleteDocument = useDeleteDocument({
    onSuccess: () => {
      refetchDocuments();
      setIsLoading(false);
    },
    onError: () => {
      setIsLoading(false);
    },
  });

  const handleDeleteDocument = async (documentId: string) => {
    setIsLoading(true);
    await deleteDocument.mutateAsync(documentId);
  };

  console.log(data);

  const createDocumentSnapshot = async (
    uploadedFiles: UploadFileDetailType[]
  ) => {
    try {
      setIsLoading(true);
      const postData = constructPostDataForFileUpload(uploadedFiles);
      const response = await createOrUpdateDocument.mutateAsync({
        data: postData,
      });

      const { documents } = response;
      if (documents?.length) {
        await Promise.all(
          documents.map((doc, index) =>
            doc.snapshot
              ? upload(uploadedFiles[index].file, doc.snapshot)
              : null
          )
        );
        await refetchDocuments();
        toast.success('File(s) uploaded successfully');
        setSelectedFiles(documents);
      }
    } catch (e) {
      toast.error('Something went wrong while uploading documents');
    } finally {
      setIsLoading(false);
    }
  };

  const upload = async (file: File, snapshot: SnapshotRequestType) => {
    const { fields, id: snapShotId, url }: SnapshotRequestType = snapshot;

    try {
      if (!file || !snapShotId) return;
      setIsLoading(true);
      toast.loading('Uploading file...');

      const formData = new FormData();
      Object.keys(fields).forEach((key) => {
        formData.append(key, fields[key]);
      });
      formData.append('file', file);

      const awsResponse = await axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (awsResponse) {
        await DocumentService.bulkUploadFinish([snapShotId]);
        SnapshotService.parseSnapshot(snapshot.id, true);
        toast.dismiss();
      }
    } catch (error: any) {
      toast.dismiss();
      toast.error(error.message || 'Error uploading file');
    } finally {
      setIsLoading(false);
    }
  };

  const onFileSelect = (files: File[], document: Partial<DocumentDataType>) => {
    if (!files.length || !document) {
      toast.error('Something went wrong, please try again.');
      return;
    }

    const fileDetails = files.map((file) => ({
      file,
      batchId: document?.batch_id,
      docId: document?.id,
      name: file.name,
      orgId: orgId,
    }));

    createDocumentSnapshot(fileDetails);
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length) {
      const dummyDocument = {
        batch_id: null,
        id: null,
      };
      onFileSelect(acceptedFiles, dummyDocument);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      hideCloseButton
      classNames={{
        wrapper: '!items-start !justify-end',
        base: '!m-0 rounded-medium h-[calc(100vh-64px)] w-full sm:w-[400px] ',
        backdrop: 'hidden',
      }}
      motionProps={{
        variants: {
          enter: {
            x: 0,
            transition: {
              duration: 0.3,
            },
          },
          exit: {
            x: 400,
            transition: {
              duration: 0.2,
              ease: 'easeIn',
            },
          },
        },
      }}
    >
      <ModalContent className="absolute top-2 right-2 h-[calc(100vh-20px)]">
        {(onClose) => (
          <>
            <ModalHeader className="flex justify-between items-center border-b">
              <span>Settings</span>
              <Button isIconOnly variant="light" onPress={onClose}>
                <Icon icon="mdi:close" className="h-5 w-5" />
              </Button>
            </ModalHeader>

            <ModalBody className="overflow-y-auto">
              <Tabs aria-label="Settings tabs" fullWidth>
                <Tab key="files" title="Files">
                  <div>
                    <h3 className="text-medium font-semibold mb-4">
                      File Upload
                    </h3>

                    <div
                      {...getRootProps()}
                      className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                        ${isDragActive ? 'border-primary bg-primary/10' : 'border-neutral-300'}`}
                    >
                      <input {...getInputProps()} />
                      {isDragActive ? (
                        <p>Drop the files here ...</p>
                      ) : (
                        <div>
                          <p>
                            Drag 'n' drop some files here, or click to select
                            files
                          </p>
                          <p className="text-sm text-neutral-500 mt-2">
                            Supported files: PDF, DOC, DOCX, TXT
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-8">
                    <h3 className="text-medium font-semibold mb-4">
                      Recent Documents
                    </h3>
                    <div className="space-y-3">
                      {data?.results.map((document) => (
                        <div
                          key={document.id}
                          className="flex items-center justify-between px-3 py-2 rounded-lg border border-neutral-200 flex-wrap cursor-pointer hover:bg-default-100"
                          onClick={() => {
                            if (selectedFiles) {
                              const isDuplicate = selectedFiles.some(
                                (file) => file.id === document.id
                              );
                              if (!isDuplicate) {
                                setSelectedFiles([...selectedFiles, document]);
                              } else {
                                toast.error('Document already selected');
                              }
                            } else {
                              setSelectedFiles([document]);
                            }
                          }}
                        >
                          <div className="flex items-center gap-3 flex-wrap  w-[80%]">
                            <Icon
                              icon="mdi:file-document-outline"
                              className="h-5 w-5 text-neutral-500"
                            />
                            <span className="text-sm truncate w-[80%]">
                              {document.filename || 'Untitled Document'}
                            </span>
                          </div>
                          <Button
                            isIconOnly
                            size="sm"
                            variant="light"
                            onPress={() => handleDeleteDocument(document.id)}
                          >
                            <Icon
                              icon="mdi:delete-outline"
                              className="h-4 w-4"
                            />
                          </Button>
                        </div>
                      ))}
                      {!data?.results.length && (
                        <p className="text-sm text-neutral-500 text-center py-4">
                          No documents uploaded yet
                        </p>
                      )}
                    </div>
                  </div>
                </Tab>
                <Tab key="tools" title="Tools">
                  <div>
                    {/* Add your files list/management UI here */}
                    <p>Tools are coming soon...</p>
                  </div>
                </Tab>
              </Tabs>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
